import React, { useContext } from 'react';
import { useHelmet, RedirectTag } from '@thd-nucleus/thd-helmet';
import {
  params,
  string,
  arrayOf,
  shape,
  useDataModel
} from '@thd-nucleus/data-sources';
import {
  getStoreIdFromStoreDetailsPath,
  invalidStoreIdHandler,
  isPathFormatSupported,
  isStoreIdValid,
  notFoundHandler,
  invalidPathHandler
} from './rules/store-finder-redirect-rules';
import { RedirectContext } from './RedirectContext';
import { handleErrors, ERROR_HANDLING_EXPERIENCE } from '../utils/redirector-utils';

const storeFinderHandler = ({ data, error: err, props }) => {
  if (!isPathFormatSupported(props.path)) {
    const invalidPath = invalidPathHandler({ path: props.path });
    if (invalidPath) {
      return new RedirectTag(invalidPath, props.bypass);
    }
    return null;
  }

  const invalidStoreId = invalidStoreIdHandler({ storeId: props.storeId });
  if (invalidStoreId) {
    return new RedirectTag(invalidStoreId, props.bypass);
  }

  const errors = handleErrors(err, ERROR_HANDLING_EXPERIENCE.GENERIC);
  if (errors) {
    return new RedirectTag(errors, props.bypass);
  }

  const notFound = notFoundHandler({ data, storeId: props.storeId });
  if (notFound) {
    return new RedirectTag(notFound, props.bypass);
  }

  return null;
};

export const StoreFinderRedirector = () => {
  const { pathWithQueryParams, bypass } = useContext(RedirectContext);

  const [path] = pathWithQueryParams?.split('?') || '';

  const storeId = getStoreIdFromStoreDetailsPath(path);

  const isStoreIdFormatValid = isStoreIdValid(storeId);

  const opts = {
    variables: {
      storeSearchInput: storeId,
      pagesize: '1',
    },
    skip: !isStoreIdFormatValid
  };
  const { data, error } = useDataModel('storeSearch', opts);

  const handlerProps = {
    path,
    storeId,
    bypass
  };

  useHelmet('storeFinderRedirector',
    { data, error, props: handlerProps },
    storeFinderHandler,
    [storeId]);

  return null;
};

StoreFinderRedirector.displayName = 'StoreFinderRedirector';

StoreFinderRedirector.dataModel = {
  storeSearch: params({
    storeSearchInput: string(),
    pagesize: string(),
  }).shape({
    stores: arrayOf(shape({
      storeId: string()
    }))
  })
};
